const DARK_STYLE_URL = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';
const LIGHT_STYLE_URL = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';

// Background colours that match our themed map so the instant style (mounted
// before the tile style finishes downloading) blends seamlessly into the
// venue card surface.
const INSTANT_BG_DARK = '#273252';
const INSTANT_BG_LIGHT = '#EAF0FF';

const styleCache = new Map();
// Holds in-flight Promises so concurrent callers share a single network round
// trip instead of issuing duplicate fetches.
const inflightFetches = new Map();

const deepClone = (value) => JSON.parse(JSON.stringify(value));

const updateLayer = (layers, ids, updater) => {
  ids.forEach((id) => {
    const layer = layers.find((entry) => entry.id === id);
    if (layer) updater(layer);
  });
};

const setVisibility = (layer, visibility) => {
  layer.layout = { ...(layer.layout || {}), visibility };
};

const setPaint = (layer, nextPaint) => {
  layer.paint = { ...(layer.paint || {}), ...nextPaint };
};

const buildStops = (color) => ({ stops: [[0, color], [6, color], [10, color], [14, color], [18, color]] });

/**
 * Returns a minimal MapLibre style that paints a solid themed background
 * synchronously, without ANY network requests. Used to mount the map canvas
 * immediately on first paint so the user sees their themed surface instead of
 * a blank box while the real tile style downloads in the background.
 */
export function getInstantMapStyle(theme = 'dark') {
  const bg = theme === 'dark' ? INSTANT_BG_DARK : INSTANT_BG_LIGHT;
  return {
    version: 8,
    sources: {},
    layers: [
      { id: 'allure-instant-background', type: 'background', paint: { 'background-color': bg } },
    ],
  };
}

const applyDarkAllureMapStyle = (style) => {
  const layers = style.layers || [];

  updateLayer(layers, ['background'], (layer) => setPaint(layer, { 'background-color': '#273252' }));
  updateLayer(layers, ['landcover', 'landuse', 'park_national_park', 'park_nature_reserve'], (layer) => setPaint(layer, { 'fill-color': buildStops('#136553') }));
  updateLayer(layers, ['landuse_residential'], (layer) => setPaint(layer, { 'fill-color': buildStops('rgba(50, 58, 79, 0.96)') }));
  updateLayer(layers, ['water'], (layer) => setPaint(layer, { 'fill-color': '#243A92' }));
  updateLayer(layers, ['water_shadow'], (layer) => setPaint(layer, { 'fill-color': '#243A92' }));
  updateLayer(layers, ['waterway'], (layer) => setPaint(layer, { 'line-color': '#3159D0' }));

  updateLayer(layers, ['boundary_county', 'boundary_state', 'boundary_country_outline', 'boundary_country_inner'], (layer) => setPaint(layer, { 'line-color': '#4E69BC' }));

  updateLayer(layers, [
    'tunnel_service_case', 'tunnel_minor_case', 'tunnel_sec_case', 'tunnel_pri_case', 'tunnel_trunk_case', 'tunnel_mot_case',
    'road_service_case', 'road_minor_case', 'road_pri_case_ramp', 'road_trunk_case_ramp', 'road_mot_case_ramp',
    'road_sec_case_noramp', 'road_pri_case_noramp', 'road_trunk_case_noramp', 'road_mot_case_noramp',
    'bridge_service_case', 'bridge_minor_case', 'bridge_sec_case', 'bridge_pri_case', 'bridge_trunk_case', 'bridge_mot_case',
  ], (layer) => setPaint(layer, { 'line-color': buildStops('#48556F') }));

  updateLayer(layers, [
    'road_minor_fill', 'road_sec_fill_noramp', 'road_trunk_fill_noramp', 'road_mot_fill_noramp',
    'bridge_trunk_fill', 'bridge_mot_fill', 'tunnel_pri_fill', 'tunnel_mot_fill',
  ], (layer) => setPaint(layer, { 'line-color': buildStops('#8A95B0') }));

  updateLayer(layers, ['road_pri_fill_noramp'], (layer) => setPaint(layer, { 'line-color': buildStops('#D3DBF1') }));
  updateLayer(layers, ['bridge_pri_fill', 'bridge_sec_fill', 'tunnel_sec_fill', 'road_service_fill', 'road_minor_fill'], (layer) => setPaint(layer, { 'line-color': buildStops('#7F8BA8') }));
  updateLayer(layers, ['aeroway-runway', 'aeroway-taxiway'], (layer) => setPaint(layer, { 'line-color': '#8B97B3' }));

  updateLayer(layers, ['building', 'building-top'], (layer) => setVisibility(layer, 'none'));
  updateLayer(layers, ['roadname_minor', 'roadname_sec', 'roadname_pri', 'roadname_major', 'poi_stadium'], (layer) => setVisibility(layer, 'none'));

  updateLayer(layers, ['poi_park'], (layer) => {
    setPaint(layer, { 'text-color': '#35A06C', 'icon-color': '#35A06C' });
    layer.layout = { ...(layer.layout || {}), 'text-size': 14 };
  });

  updateLayer(layers, ['watername_ocean', 'watername_sea', 'watername_lake', 'watername_lake_line'], (layer) => setPaint(layer, { 'text-color': '#5878DF' }));
  updateLayer(layers, ['place_city_r6', 'place_city_r5', 'place_city_dot_r7', 'place_city_dot_r4', 'place_city_dot_r2', 'place_city_dot_z7'], (layer) => {
    setPaint(layer, { 'text-color': '#F4F7FF', 'text-halo-color': 'rgba(42, 56, 101, 0.55)', 'text-halo-width': 1.3 });
  });
  updateLayer(layers, ['place_town', 'place_suburbs', 'place_villages', 'place_hamlet', 'place_state', 'place_country_1', 'place_country_2', 'place_continent'], (layer) => {
    setPaint(layer, { 'text-color': '#DDE5F8', 'text-halo-color': 'rgba(42, 56, 101, 0.55)', 'text-halo-width': 1 });
  });

  return style;
};

const applyLightAllureMapStyle = (style) => {
  const layers = style.layers || [];
  updateLayer(layers, ['water'], (layer) => setPaint(layer, { 'fill-color': '#7FB9FF' }));
  updateLayer(layers, ['waterway'], (layer) => setPaint(layer, { 'line-color': '#4E97F0' }));
  updateLayer(layers, ['landcover', 'landuse', 'park_national_park', 'park_nature_reserve'], (layer) => setPaint(layer, { 'fill-color': buildStops('#BFE7C9') }));
  return style;
};

export async function getAllureMapStyle(theme = 'dark') {
  const cacheKey = theme === 'dark' ? 'dark' : 'light';
  if (styleCache.has(cacheKey)) {
    return deepClone(styleCache.get(cacheKey));
  }

  // De-dupe concurrent fetches: if a request is already in-flight for this
  // theme, return that Promise instead of starting another one.
  if (inflightFetches.has(cacheKey)) {
    const themedStyle = await inflightFetches.get(cacheKey);
    return deepClone(themedStyle);
  }

  const fetchPromise = (async () => {
    const response = await fetch(theme === 'dark' ? DARK_STYLE_URL : LIGHT_STYLE_URL);
    const style = await response.json();
    const themedStyle = theme === 'dark' ? applyDarkAllureMapStyle(style) : applyLightAllureMapStyle(style);
    styleCache.set(cacheKey, deepClone(themedStyle));
    return themedStyle;
  })();

  inflightFetches.set(cacheKey, fetchPromise);
  try {
    const themedStyle = await fetchPromise;
    return deepClone(themedStyle);
  } finally {
    inflightFetches.delete(cacheKey);
  }
}

/**
 * Fire-and-forget prefetch. Warms the in-memory cache so a later
 * `getAllureMapStyle()` call resolves synchronously. Safe to call repeatedly
 * — concurrent calls share a single network round trip.
 */
export function prefetchAllureMapStyle(theme = 'dark') {
  const cacheKey = theme === 'dark' ? 'dark' : 'light';
  if (styleCache.has(cacheKey) || inflightFetches.has(cacheKey)) return;
  // Swallow rejections so a network blip never bubbles as an unhandled error.
  getAllureMapStyle(theme).catch(() => {});
}

export function createSmallDotMarkerElement({
  isSelected = false,
  isFavorite = false,
  testId,
  micro = false,
} = {}) {
  // Minimal dot marker used for venues OUTSIDE the user's 20km radius OR when
  // the map is zoomed out far enough that pin photos would clutter the view.
  // `micro = true` renders a tiny shiny gradient dot used at very low zoom
  // levels so dense clusters still read as individual venues.
  //
  // ARCHITECTURE NOTE: MapLibre uses the OUTER element's `transform` to
  // position the marker geographically. We MUST NOT set `transform` on the
  // outer element — instead all visual effects live on `inner` so MapLibre's
  // positioning is never clobbered when we toggle the selected state.
  const button = document.createElement('button');
  button.type = 'button';
  if (testId) button.dataset.testid = testId;
  if (isFavorite) button.dataset.favorite = '1';
  if (micro) button.dataset.micro = '1';
  // No `position` on the outer element — MapLibre owns it (absolute).
  button.style.background = 'transparent';
  button.style.border = 'none';
  button.style.padding = '0';
  button.style.cursor = 'pointer';
  button.style.outline = 'none';
  // Outer dimensions fixed; the visual size lives on `inner` which scales.
  const outerSize = micro ? 12 : 18;
  button.style.width = `${outerSize}px`;
  button.style.height = `${outerSize}px`;
  button.style.display = 'flex';
  button.style.alignItems = 'center';
  button.style.justifyContent = 'center';

  const inner = document.createElement('div');
  inner.dataset.markerInner = '1';
  const baseSize = micro ? 7 : 14;
  const selectedSize = micro ? 10 : 18;
  const size = isSelected ? selectedSize : baseSize;
  inner.style.width = `${size}px`;
  inner.style.height = `${size}px`;
  inner.style.borderRadius = '50%';
  if (micro) {
    inner.style.background = isSelected
      ? 'radial-gradient(circle at 30% 28%, #D7CCFF 0%, #8E73FF 55%, #5A3DE4 100%)'
      : 'radial-gradient(circle at 30% 28%, #C6B7FF 0%, #7C62F2 55%, #4F32CF 100%)';
    inner.style.border = '1px solid rgba(255,255,255,0.95)';
    inner.style.boxShadow = isSelected
      ? '0 0 0 3px rgba(179, 160, 255, 0.32), 0 2px 8px rgba(8, 12, 24, 0.32)'
      : '0 0 0 2px rgba(179, 160, 255, 0.20), 0 2px 6px rgba(8, 12, 24, 0.28)';
  } else {
    inner.style.background = isSelected
      ? 'rgba(113, 90, 247, 0.95)'
      : 'rgba(113, 90, 247, 0.55)';
    inner.style.border = '2px solid rgba(255,255,255,0.92)';
    inner.style.boxShadow = isSelected
      ? '0 0 0 5px rgba(113, 90, 247, 0.20), 0 6px 14px rgba(8, 12, 24, 0.24)'
      : '0 4px 8px rgba(8, 12, 24, 0.22)';
  }
  inner.style.transition = 'transform 220ms cubic-bezier(.22,1,.36,1), background 220ms ease';
  inner.style.transform = isSelected ? 'scale(1.15)' : 'scale(1)';
  button.appendChild(inner);
  if (isFavorite && !micro) {
    button.appendChild(buildFavoriteHeartBadge({ small: true }));
  }
  return button;
}

// Small red heart badge that sits on the edge of a venue marker. Used to give
// the user a quick visual cue that they've already saved this venue.
function buildFavoriteHeartBadge({ small = false } = {}) {
  const badge = document.createElement('div');
  badge.dataset.favoriteBadge = '1';
  const diameter = small ? 13 : 18;
  badge.style.position = 'absolute';
  badge.style.top = small ? '-6px' : '-4px';
  badge.style.right = small ? '-6px' : '-4px';
  badge.style.width = `${diameter}px`;
  badge.style.height = `${diameter}px`;
  badge.style.borderRadius = '50%';
  badge.style.background = '#FFFFFF';
  badge.style.display = 'flex';
  badge.style.alignItems = 'center';
  badge.style.justifyContent = 'center';
  badge.style.boxShadow = '0 4px 8px rgba(229, 48, 79, 0.32)';
  badge.style.pointerEvents = 'none';
  const iconSize = small ? 7 : 10;
  badge.innerHTML = `<svg width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="#E5304F" stroke="#E5304F" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`;
  return badge;
}

export function createMapMarkerElement({ imageUrl, size = 46, sizeScale = 1, isSelected = false, isFavorite = false, testId, eventState = null }) {
  // ANCHOR STABILITY — ROUND 7 (visual polish restored on top of the
  // round-6 architectural fix).
  //
  // Round-6 proved that stripping the compositor-layer hints
  // (`translateZ(0)`, `willChange: transform`, `backfaceVisibility:
  // hidden`) is what killed the Android-WebView pin drift during
  // pinch-zoom. Those hints promoted the scaled wrap to its own GPU
  // layer, which re-rasterized at subpixel-rounded offsets every
  // zoom frame and made the pin appear to swim across the geographic
  // surface. The box-shadow / border / selected-ring were only the
  // VISIBLE decoration that made the drift readable — not the cause.
  //
  // Round-7 brings every visual effect back (shadow, white border,
  // selected-state outer ring) while STRICTLY avoiding any compositor
  // promotion on the inner wrap:
  //   • NO `translateZ(0)` on the transform
  //   • NO `willChange: transform`
  //   • NO `backfaceVisibility: hidden`
  // The wrap stays on MapLibre's own compositor layer, so scaling
  // happens via CPU repaint on the same layer that holds the geo
  // positioning transform — no subpixel offsets per frame, no drift.
  const el = document.createElement('div');
  el.setAttribute('data-testid', testId);
  if (eventState) el.dataset.eventState = eventState;
  if (isFavorite) el.dataset.favorite = '1';
  el.style.cursor = 'pointer';
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.transformOrigin = 'center';

  const wrap = document.createElement('div');
  wrap.setAttribute('data-marker-inner', '');
  wrap.style.width = `${size}px`;
  wrap.style.height = `${size}px`;
  wrap.style.borderRadius = '50%';
  wrap.style.overflow = 'hidden';
  wrap.style.boxSizing = 'border-box';
  // White ring + drop shadow for depth. These extend OUTSIDE the 46×46
  // bounds visually but they're painted on the same compositor layer
  // as the parent (no translateZ / willChange), so no subpixel drift.
  wrap.style.border = isSelected ? '3px solid #EAF0FF' : '3px solid rgba(255,255,255,0.92)';
  wrap.style.boxShadow = isSelected
    ? '0 0 0 4px rgba(113, 90, 247, 0.28), 0 6px 14px rgba(8, 12, 24, 0.24)'
    : '0 6px 14px rgba(8, 12, 24, 0.24)';
  // Fallback colour matches `#1D2340` so the pin never flashes a
  // different hue if the image fails to load.
  wrap.style.backgroundColor = '#1D2340';
  wrap.style.transformOrigin = 'center center';
  const effectiveScale = (isSelected ? 1.1 : 1) * sizeScale;
  // Pure CPU-layer scale. Do NOT add `translateZ(0)` here — the GPU
  // layer promotion is what caused the native-Android pin drift.
  wrap.style.transform = `scale(${effectiveScale})`;
  wrap.style.transition = 'transform 160ms ease, box-shadow 200ms ease';
  wrap.dataset.sizeScale = String(sizeScale);
  wrap.dataset.selected = isSelected ? '1' : '0';

  // ANCHOR STABILITY — ROUND 8 (support guidance) + ROUND 9 (sizing fix).
  // Replace the raster `background-image` with a child <img>. On Android
  // WebView, `background-image: url(...)` combined with the inner
  // wrap's `scale()` transform forced a GPU texture re-sample at
  // fractional sizes every zoom frame, which read as per-pin texture
  // jitter. The img rasterizes once at natural size and the single
  // `scale()` on the wrap scales img+border+shadow together — no
  // per-frame re-sampling. Scale/zoom logic is UNCHANGED: the parent
  // wrap's `scale()` still handles every size change exactly as before.
  //
  // Size: 100%/100% + maxWidth:none so the Tailwind preflight rule
  // `img { max-width: 100% }` can't narrow the img against the wrap's
  // content-box width (border-box + 3px border). The parent wrap owns
  // the 46×46 outer size; the img fills its 40×40 content box.
  const photo = document.createElement('img');
  photo.src = imageUrl;
  photo.alt = '';
  photo.draggable = false;
  photo.style.width = '100%';
  photo.style.height = '100%';
  photo.style.maxWidth = 'none';
  photo.style.objectFit = 'cover';
  photo.style.borderRadius = '50%';
  photo.style.display = 'block';
  photo.style.pointerEvents = 'none';
  wrap.appendChild(photo);

  el.appendChild(wrap);
  return el;
}

export function createUserMarkerElement(photoUrl, { isLive = true } = {}) {
  // Container holds the pulsing halo + the inner "you-are-here" dot. Both
  // visual treatments are intentionally NOT shared with venue markers so the
  // user can instantly tell themselves apart from venues on the map.
  const container = document.createElement('div');
  container.dataset.testid = 'guest-map-user-marker';
  container.dataset.locationStatus = isLive ? 'live' : 'fallback';
  // No `position` on the outer element — MapLibre's `.maplibregl-marker`
  // rule makes it absolute, which is also what the absolute halo/ring
  // children anchor against. An inline `relative` put the blue dot into
  // normal flow and shifted every venue pin below it by 32px in prod.
  container.style.width = '32px';
  container.style.height = '32px';
  container.style.display = 'flex';
  container.style.alignItems = 'center';
  container.style.justifyContent = 'center';
  container.style.pointerEvents = 'none';

  // Outer continuously-pulsing halo — a signature Google-Maps-style "ping"
  // that venue pins never use, so it's instantly readable as the user.
  // Only rendered when we have LIVE location: it doubles as a "confident
  // tracking" indicator, so a fallback (Corfu default) marker stays static
  // to hint the user that their real location isn't being tracked.
  if (isLive) {
    const halo = document.createElement('div');
    halo.style.position = 'absolute';
    halo.style.width = '60px';
    halo.style.height = '60px';
    halo.style.borderRadius = '50%';
    halo.style.background = 'radial-gradient(circle, rgba(56, 132, 255, 0.45) 0%, rgba(56, 132, 255, 0.18) 50%, rgba(56, 132, 255, 0) 72%)';
    halo.style.animation = 'allure-user-location-pulse 1.8s ease-in-out infinite';
    halo.style.pointerEvents = 'none';
    container.appendChild(halo);
  }

  // Mid ring — soft white outline that visually "lifts" the dot off the map.
  const ring = document.createElement('div');
  ring.style.position = 'absolute';
  ring.style.width = '28px';
  ring.style.height = '28px';
  ring.style.borderRadius = '50%';
  ring.style.background = '#FFFFFF';
  ring.style.boxShadow = isLive
    ? '0 4px 12px rgba(8, 12, 24, 0.32), 0 0 0 1px rgba(56, 132, 255, 0.25)'
    : '0 4px 10px rgba(8, 12, 24, 0.28), 0 0 0 1px rgba(255, 255, 255, 0.22)';
  container.appendChild(ring);

  // Inner solid dot — blue when live, muted grey when we're on the fallback
  // centre. The colour cue tells the user "this isn't really where you are".
  const dot = document.createElement('div');
  dot.style.position = 'relative';
  dot.style.width = '18px';
  dot.style.height = '18px';
  dot.style.borderRadius = '50%';
  dot.style.background = isLive
    ? 'radial-gradient(circle at 32% 30%, #6BA8FF 0%, #3884FF 55%, #1F62D6 100%)'
    : 'radial-gradient(circle at 32% 30%, #B8BBC5 0%, #7A7F8B 55%, #4A4F5A 100%)';
  dot.style.boxShadow = isLive
    ? 'inset 0 1px 2px rgba(255,255,255,0.55), 0 2px 4px rgba(31, 98, 214, 0.4)'
    : 'inset 0 1px 2px rgba(255,255,255,0.45), 0 2px 4px rgba(0, 0, 0, 0.28)';
  container.appendChild(dot);

  // photoUrl intentionally ignored — keeping the user marker abstract (no
  // avatar) is what makes it unambiguously distinct from venue pins.
  void photoUrl;
  return container;
}